import { useState, useRef } from "react";
import { GroupedInvestmentCategory } from "../../types";
import { ROLES_ENUM, SVG_ENUM } from "enums";
import {
  ASSET_CATEGORY_NAMES,
  AssetCategorySlug,
} from "enums/AssetCategoryEnum";
import SVG from "shared/SVG";
import { Tooltip } from "react-tooltip";
import ContextMenu from "shared/ContextMenu";
import InvestmentPhotoLightbox from "../InvestmentPhotoLightbox/InvestmentPhotoLightbox";
import InvestmentTransactionsSideModal from "../InvestmentTransactionsSideModal/InvestmentTransactionsSideModal";
import InvestmentPolicyDetailsSideModal from "../InvestmentPolicyDetailsSideModal/InvestmentPolicyDetailsSideModal";
import TreasuryBondsDetailsSideModal from "../TreasuryBondsDetailsSideModal/TreasuryBondsDetailsSideModal";
import ArtworkDetailsSideModal from "../ArtworkDetailsSideModal/ArtworkDetailsSideModal";
import PreciousStonesDetailsSideModal from "../PreciousStonesDetailsSideModal/PreciousStonesDetailsSideModal";
import RealEstateDetailsSideModal from "../RealEstateDetailsSideModal/RealEstateDetailsSideModal";
import OtherAssetDetailsSideModal from "../OtherAssetDetailsSideModal/OtherAssetDetailsSideModal";
import AddInvestmentTransactionModal from "../AddInvestmentTransactionModal/AddInvestmentTransactionModal";
import CloseInvestmentPolicyModal from "../CloseInvestmentPolicyModal/CloseInvestmentPolicyModal";
import CloseAssetModal from "../CloseAssetModal/CloseAssetModal";
import CloseTreasuryBondsModal from "../CloseTreasuryBondsModal/CloseTreasuryBondsModal";
import CloseLoanModal from "../CloseLoanModal/CloseLoanModal";
import CloseRealEstateModal from "../CloseRealEstateModal/CloseRealEstateModal";
import CloseOtherAssetModal from "../CloseOtherAssetModal/CloseOtherAssetModal";
import LoanDetailsSideModal from "../LoanDetailsSideModal/LoanDetailsSideModal";
import DeleteInvestmentModal from "../DeleteInvestmentModal/DeleteInvestmentModal";
import EditInvestmentModal from "../EditInvestmentModal/EditInvestmentModal";
import { InvestmentTransactionType } from "hooks/investments/GetInvestmentTransactionsHook";
import { useGetIndustriesHook } from "hooks/industries";
import { useGetMe } from "hooks/auth";
import TransactionsSideModal from "features/companies/TransactionsSideModal/TransactionsSideModal";
import {
  AddCompanyModal,
  AddCompanyClientModal,
  DeleteCompanyModal,
  SellSharesModal,
} from "features/companies";
import { AddSharesModal } from "features/shares";
import "./AssetTile.scss";

const SLUGS_WITH_TRANSACTIONS = [
  // "stocks",
  // "shares",
  "crypto",
  "currencies",
  // "treasury_bonds",
  "precious_metals",
  // "precious_stones",
  // "real_estate",
  // "artwork",
  // "loans",
  // "investment_policy",
];

interface AssetTileProps {
  category: GroupedInvestmentCategory;
  index: number;
  isOwn?: boolean;
  onToggleExpand: (slug: string, isExpanded: boolean) => void;
  onDragStart: (index: number) => void;
  onDragEnter: (index: number) => void;
  onDragEnd: () => void;
  onTouchStart: (index: number) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onTouchEnd: () => void;
  isDragging: boolean;
  isDragOver: boolean;
}

const AssetTile = ({
  category,
  index,
  isOwn = false,
  onToggleExpand,
  onDragStart,
  onDragEnter,
  onDragEnd,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  isDragging,
  isDragOver,
}: AssetTileProps) => {
  const { investmentType, isExpanded, items } = category;
  const [lightboxItem, setLightboxItem] = useState<any>(null);
  const [selectedTransactionItem, setSelectedTransactionItem] =
    useState<any>(null);
  const [selectedArtworkItem, setSelectedArtworkItem] = useState<any>(null);
  const [selectedPreciousStonesItem, setSelectedPreciousStonesItem] =
    useState<any>(null);
  const [selectedRealEstateItem, setSelectedRealEstateItem] =
    useState<any>(null);
  const [selectedOtherItem, setSelectedOtherItem] = useState<any>(null);
  const [selectedPolicyItem, setSelectedPolicyItem] = useState<any>(null);
  const [selectedTreasuryBondsItem, setSelectedTreasuryBondsItem] =
    useState<any>(null);
  const [addTransactionItem, setAddTransactionItem] = useState<{
    item: any;
    defaultType: "buy" | "sell";
    currentQuantity?: number;
  } | null>(null);
  const [editTransactionItem, setEditTransactionItem] = useState<{
    item: any;
    tx: InvestmentTransactionType;
    currentQuantity: number;
  } | null>(null);
  const [closeInvestmentPolicyItem, setCloseInvestmentPolicyItem] =
    useState<any>(null);
  const [closeAssetItem, setCloseAssetItem] = useState<any>(null);
  const [closeTreasuryBondsItem, setCloseTreasuryBondsItem] =
    useState<any>(null);
  const [closeRealEstateItem, setCloseRealEstateItem] = useState<any>(null);
  const [closeLoanItem, setCloseLoanItem] = useState<any>(null);
  const [closeOtherItem, setCloseOtherItem] = useState<any>(null);
  const [selectedLoanItem, setSelectedLoanItem] = useState<any>(null);
  const [deleteItem, setDeleteItem] = useState<any>(null);
  const [deleteShareItem, setDeleteShareItem] = useState<any>(null);
  const [editItem, setEditItem] = useState<any>(null);
  const [selectedShareItem, setSelectedShareItem] = useState<any>(null);
  const [sellSharesItem, setSellSharesItem] = useState<any>(null);
  const [editSharesItem, setEditSharesItem] = useState<any>(null);
  const [editCompanyItem, setEditCompanyItem] = useState<any>(null);
  // ref pozwalający wrócić do sidebara po zamknięciu modali buy/sell/edit
  const shareParentRef = useRef<any>(null);

  const isStocksOrShares =
    investmentType.slug === "stocks" || investmentType.slug === "shares";

  const { data: authorizedUser } = useGetMe();
  const isAdmin = authorizedUser?.role === ROLES_ENUM.ADMIN;

  const { data: industriesData } = useGetIndustriesHook();
  const industryMap: Record<string, string> = {};
  if (industriesData) {
    for (const ind of industriesData) {
      industryMap[ind.value] = ind.label;
    }
  }

  const openSellModal = (shareItem: any) => {
    shareParentRef.current = shareItem;
    setSelectedShareItem(null);
    setTimeout(() => setSellSharesItem(shareItem), 100);
  };

  const openEditSharesModal = (company: any) => {
    shareParentRef.current = company;
    setSelectedShareItem(null);
    setTimeout(() => setEditSharesItem(company), 100);
  };

  const returnToSidebar = (data?: any) => {
    if (data?.availableShares !== undefined && shareParentRef.current) {
      shareParentRef.current = {
        ...shareParentRef.current,
        sharesCount: data.availableShares,
      };
    }
    setSellSharesItem(null);
    setEditSharesItem(null);
    if (shareParentRef.current)
      setTimeout(
        () => setSelectedShareItem({ ...shareParentRef.current }),
        100,
      );
  };

  const hasTransactions = SLUGS_WITH_TRANSACTIONS.includes(investmentType.slug);

  const handleToggle = () => {
    onToggleExpand(investmentType.slug, !isExpanded);
  };

  type ColumnDef = {
    key: string;
    width?: number;
    suffix?: string;
    computeSuffix?: (item: any) => string;
    computePrefix?: (item: any) => string;
    label?: string;
    value?: string;
  };

  const getColumns = (): ColumnDef[] => {
    switch (investmentType.slug) {
      case "stocks":
      case "shares":
        return [
          { key: "name", width: 200, label: "Nazwa spółki" },
          { key: "industry", width: 150, label: "Branża" },
          { key: "sharesCount", width: 120, label: "Ilość sztuk" },
          {
            key: "sharesValue",
            width: 160,
            label: "Koszt nabycia",
          },
          { key: "currencyUuid", width: 100 },
          {
            key: "publicationDate",
            width: 130,
            label: "Data publikacji na giełdzie",
          },
        ];
      case "crypto":
        return [
          { key: "name", width: 200, label: "Nazwa" },
          { key: "quantity", width: 130, label: "Ilość" },
          { key: "currencyUuid", width: 100 },
        ];
      case "currencies":
        return [
          { key: "name", width: 150 },
          {
            key: "primaryValue",
            width: 200,
            label: "Kwota",
            computeSuffix: (item) =>
              item.currency?.currency ? ` ${item.currency.currency} ` : "",
            // computeSuffix: (item) => {
            //   const code = item.currency?.currency ?? "";
            //   const symbols: Record<string, string> = {
            //     USD: "$",
            //     EUR: "€",
            //     GBP: "£",
            //     CHF: "CHF",
            //     JPY: "¥",
            //   };
            //   return symbols[code] ?? code;
            // },
          },
        ];
      case "treasury_bonds":
        return [
          { key: "", width: 280, label: "Nazwa", value: "Obligacje skarbowe" },
          { key: "interestType", width: 170 },
          { key: "quantity", width: 120, label: "Ilość sztuk" },
          {
            key: "primaryValue",
            width: 130,
            label: "Koszt nabycia",
            computeSuffix: (item) =>
              item.currency ? ` ${item.currency?.currency}` : "",
          },
          { key: "currencyUuid", width: 100 },
          { key: "interestRate", width: 220 },
        ];
      case "precious_stones":
        return [
          { key: "name", width: 150 },
          { key: "primaryValue", width: 150, label: "Cena zakupu" },
          { key: "currencyUuid", width: 100 },
          { key: "weight", width: 100 },
          { key: "cut", width: 100 },
          { key: "color", width: 100 },
          { key: "clarity", width: 100 },
        ];
      case "precious_metals":
        return [
          { key: "name", width: 200 },
          { key: "primaryValue", width: 200 },
          { key: "currencyUuid", width: 100 },
          { key: "weight", width: 120 },
          { key: "finess", width: 100 },
        ];
      case "artwork":
        return [
          { key: "name", width: 200 },
          { key: "primaryValue", width: 200 },
          { key: "currencyUuid", width: 100 },
          { key: "author", width: 200 },
          { key: "eraAge", width: 150 },
        ];
      case "real_estate":
        return [
          { key: "name", width: 200 },
          { key: "primaryValue", width: 180, label: "Cena zakupu" },
          { key: "currencyUuid", width: 150 },
          { key: "city", width: 150, label: "Miejscowość" },
          { key: "area", width: 200 },
          { key: "pricePerSqm", width: 180, label: "Cena za m2:" },
        ];
      case "loans":
        return [
          { key: "entity", width: 200, label: "Podmiot" },
          { key: "loanType", width: 130, label: "Rodzaj" },
          {
            key: "primaryValue",
            width: 120,
            label: "Kwota",
            computeSuffix: (item) =>
              item.currency?.currency ? ` ${item.currency.currency}` : "",
          },
          { key: "interestRate", width: 130 },
          { key: "interestType", width: 115 },
          { key: "interestPayment" },
          { key: "contractDate" },
          { key: "durationDate", label: "Czas trwania" },
        ];
      case "investment_policy":
        return [
          { key: "name", width: 200 },
          { key: "currencyUuid", width: 100 },
          { key: "contractDate", width: 150 },
          { key: "durationDate", width: 150 },
          { key: "premiumPayment", width: 150 },
        ];
      default:
        return [
          { key: "name", width: 200 },
          { key: "primaryValue", width: 150 },
          { key: "currencyUuid", width: 150 },
        ];
    }
  };

  const columnLabels: Record<string, string> = {
    quantity: "Ilość sztuk",
    name: "Nazwa",
    purchaseRate: "Kurs zakupu",
    primaryValue: "Koszt nabycia",
    currencyUuid: "Waluta",
    industry: "Branża",
    publicationDate: "Data publikacji na giełdzie",
    area: "Wielkość [w m2]",
    entity: "Podmiot",
    loanType: "Rodzaj",
    interestRate: "Wysokość oprocentowania",
    interestType: "Rodzaj oprocentowania",
    interestPayment: "Odsetki",
    weight: "Masa",
    cut: "Szlif",
    color: "Barwa",
    clarity: "Czystość",
    finess: "Próba",
    author: "Autor",
    eraAge: "Epoka/wiek",
    contractDate: "Data zawarcia",
    durationDate: "Czas trwania",
    premiumPayment: "Składki",
    autoRenewal: "Automatyczne przedłużenie",
  };

  const columns = getColumns();
  const hasFixedWidths = columns.some((col) => col.width !== undefined);

  const INTEREST_TYPE_LABELS: Record<string, string> = {
    fixed: "Stałe",
    variable: "Zmienne",
    periodically_fixed: "Okresowo-stałe",
  };

  const LOAN_TYPE_LABELS: Record<string, string> = {
    borrowed: "Zaciągnięta",
    lent: "Udzielona",
  };

  const PAYMENT_LABELS: Record<string, string> = {
    monthly: "Miesięczne",
    quarterly: "Kwartalne",
    yearly: "Roczne",
    at_end_of_year: "Na koniec roku",
  };

  const detailsFields = [
    "area",
    "weight",
    "cut",
    "color",
    "clarity",
    "finess",
    "author",
    "eraAge",
    "interestType",
    "interestPayment",
    "contractDate",
    "durationDate",
    "premiumPayment",
    "autoRenewal",
    "publicationDate",
  ];

  const PROPERTY_TYPE_LABELS: Record<string, string> = {
    apartment: "Mieszkanie",
    house: "Dom",
    land: "Działka",
  };

  const METAL_TYPE_LABELS: Record<string, string> = {
    gold: "Złoto",
    silver: "Srebro",
  };

  const formatValue = (item: any, column: string) => {
    if (column === "currencyUuid") {
      const currency = item.currency?.currency;
      return currency ?? "Brak";
    }

    if (column === "industry") {
      if (item.industryUuid) {
        return industryMap[item.industryUuid] ?? "Brak";
      }
      return item.industry?.name ?? item.details?.industryName ?? "Brak";
    }

    if (column === "sharesCount") {
      const val = item.sharesCount;
      if (val === null || val === undefined) return "Brak";
      return Number(val).toLocaleString("pl-PL");
    }

    if (column === "quantity") {
      const val = item.quantity;
      if (val === null || val === undefined) return "Brak";
      return Number(val).toLocaleString("pl-PL");
    }

    if (column === "sharesValue") {
      const val = item.sharesValue;
      if (val === null || val === undefined) return "Brak";
      const formatted = Number(val).toLocaleString("pl-PL", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
      const currency = item.currency?.currency ?? "";
      return currency ? `${formatted} ${currency}` : formatted;
    }

    if (column === "publicationDate" && isStocksOrShares) {
      const dateVal = item.publicationDate ?? item.details?.publicationDate;
      if (!dateVal) return "Brak";
      const d = new Date(dateVal as string);
      return isNaN(d.getTime())
        ? String(dateVal)
        : d.toLocaleDateString("pl-PL");
    }

    if (column === "name" && investmentType.slug === "currencies") {
      return item.currency?.currency ?? "Brak";
    }

    if (
      column === "interestRate" &&
      (investmentType.slug === "treasury_bonds" ||
        investmentType.slug === "loans")
    ) {
      const value = item.details?.interestRate;
      if (value === null || value === undefined || value === "") return "Brak";
      return `${value}%`;
    }

    if (column === "entity" && investmentType.slug === "loans") {
      const val = item.details?.entity ?? item.entity;
      if (val === null || val === undefined || val === "") return "Brak";
      return val;
    }

    if (column === "loanType") {
      const value = item.loanType ?? item.details?.loanType;
      if (value === null || value === undefined || value === "") return "Brak";
      return LOAN_TYPE_LABELS[value] ?? value;
    }

    if (column === "interestType") {
      const value =
        item.details?.interestRateType ?? item.details?.interestType;
      if (value === null || value === undefined || value === "") return "Brak";
      return INTEREST_TYPE_LABELS[value] ?? value;
    }

    if (column === "name" && investmentType.slug === "real_estate") {
      const propertyType = item.details?.propertyType;
      if (!propertyType) return item.name ?? "Brak";
      if (propertyType === "other") {
        return item.details?.propertyTypeOther ?? "Brak";
      }
      return PROPERTY_TYPE_LABELS[propertyType] ?? propertyType;
    }

    if (column === "name" && investmentType.slug === "precious_metals") {
      const metalType = item.details?.metalType;
      if (!metalType) return item.name ?? "Brak";
      if (metalType === "other") {
        return item.details?.metalTypeOther ?? "Brak";
      }
      return METAL_TYPE_LABELS[metalType] ?? metalType;
    }

    if (column === "city") {
      const val = item.details?.city;
      if (val === null || val === undefined || val === "") return "Brak";
      return val;
    }

    if (column === "pricePerSqm") {
      const sqmValue = item.details?.pricePerSqm;
      if (sqmValue === null || sqmValue === undefined) return "Brak";
      return Number(sqmValue).toLocaleString("pl-PL", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    }

    if (investmentType.slug === "investment_policy") {
      if (column === "contractDate") {
        const dateVal = item.details?.contractDate;
        if (!dateVal) return "Brak";
        const d = new Date(dateVal);
        return isNaN(d.getTime()) ? dateVal : d.toLocaleDateString("pl-PL");
      }

      if (column === "durationDate") {
        const dateVal = item.details?.durationDate;
        if (dateVal === null || dateVal === undefined) return "Bezterminowo";
        if (typeof dateVal === "string" && dateVal !== "") {
          const d = new Date(dateVal);
          if (isNaN(d.getTime())) return "Brak";
          if (
            d.getFullYear() === 2000 &&
            d.getMonth() === 0 &&
            d.getDate() === 1
          )
            return "Brak";
          return d.toLocaleDateString("pl-PL");
        }
        return "Brak";
      }
    }

    if (investmentType.slug === "loans") {
      if (column === "contractDate") {
        const dateVal = item.details?.contractDate;
        if (!dateVal) return "Brak";
        const d = new Date(dateVal);
        return isNaN(d.getTime()) ? dateVal : d.toLocaleDateString("pl-PL");
      }

      if (column === "durationDate") {
        const dateVal = item.details?.durationDate;
        if (dateVal === null || dateVal === undefined) return "Bezterminowo";
        if (typeof dateVal === "string" && dateVal !== "") {
          const d = new Date(dateVal);
          if (isNaN(d.getTime())) return "Brak";
          if (
            d.getFullYear() === 2000 &&
            d.getMonth() === 0 &&
            d.getDate() === 1
          )
            return "Brak";
          return d.toLocaleDateString("pl-PL");
        }
        return "Brak";
      }
    }

    const value = detailsFields.includes(column)
      ? item.details?.[column]
      : item[column];

    if (value === null || value === undefined || value === "") return "Brak";

    if (column === "interestRate") return `${value}%`;
    if (column === "interestPayment" || column === "premiumPayment")
      return PAYMENT_LABELS[value] ?? value;
    if (column === "autoRenewal")
      return value === true || value === 1 ? "Tak" : "Nie";
    if (column === "primaryValue") {
      const numVal = typeof value === "number" ? value : Number(value);
      if (!isNaN(numVal)) {
        return numVal.toLocaleString("pl-PL", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        });
      }
      return value;
    }
    if (column === "weight") {
      return typeof value === "number"
        ? `${value.toLocaleString("pl-PL")}g`
        : `${value}g`;
    }
    if (column === "area") {
      return typeof value === "number"
        ? value.toLocaleString("pl-PL", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          })
        : value;
    }
    if (column === "mass") return `${value}g`;

    return value;
  };

  return (
    <>
      <Tooltip id="asset-info-tooltip" place="top" className="tooltip" />
      <div
        className={`asset-tile ${isDragging ? "asset-tile--dragging" : ""} ${
          isDragOver ? "asset-tile--drag-over" : ""
        } ${isExpanded ? "asset-tile--expanded" : ""}`}
        draggable
        onDragStart={() => onDragStart(index)}
        onDragEnter={() => onDragEnter(index)}
        onDragEnd={onDragEnd}
        onDragOver={(e) => e.preventDefault()}
        data-tile-index={index}
        onTouchStart={() => onTouchStart(index)}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div className="asset-tile__header" onClick={handleToggle}>
          <div
            className="asset-tile__drag-handle"
            onClick={(e) => e.stopPropagation()}
          >
            <SVG type={SVG_ENUM.DRAG_HANDLE} />
          </div>
          <span className="asset-tile__title">
            {ASSET_CATEGORY_NAMES[investmentType.slug as AssetCategorySlug] ??
              investmentType.name}
          </span>
          {items.length > 0 && (
            <div
              className="asset-tile__header-actions"
              onClick={(e) => e.stopPropagation()}
            >
              <button className="asset-tile__action-btn">
                <SVG type={SVG_ENUM.ANNOUCEMENT} />
                <span className="asset-tile__action-badge">0</span>
              </button>
            </div>
          )}
          <div
            className={`asset-tile__chevron ${
              isExpanded ? "asset-tile__chevron--expanded" : ""
            }`}
          >
            <SVG type={SVG_ENUM.CHEVRON_DOWN} />
          </div>
        </div>

        <div
          className={`asset-tile__content-wrapper ${
            isExpanded ? "asset-tile__content-wrapper--expanded" : ""
          }`}
        >
          <div className="asset-tile__content-inner">
            {items.length > 0 && (
              <div className="asset-tile__content">
                <div className="asset-tile__table">
                  <div className="asset-tile__table-header">
                    {columns.map((col) => (
                      <div
                        key={col.key}
                        className="asset-tile__table-cell"
                        style={
                          col.width
                            ? {
                                flex: `0 0 ${col.width}px`,
                                minWidth: col.width,
                              }
                            : undefined
                        }
                      >
                        {col.label ?? columnLabels[col.key] ?? col.key}
                      </div>
                    ))}
                    {hasFixedWidths && <div style={{ flex: 1 }} />}
                    <div className="asset-tile__table-cell asset-tile__table-cell--actions asset-tile__table-cell--actions-desktop" />
                  </div>
                  {items.map((item) => {
                    // const photoFiles =
                    //   investmentType.slug === "artwork" ||
                    //   investmentType.slug === "real_estate"
                    //     ? (item.files ?? []).filter(
                    //         (f: any) => f.role === "photo",
                    //       )
                    //     : [];

                    const renderCellContent = (col: ColumnDef) => {
                      if (col.value !== undefined) {
                        return <>{col.value}</>;
                      }
                      if (
                        col.key === "name" &&
                        investmentType.slug === "real_estate"
                      ) {
                        return <span>{formatValue(item, col.key)}</span>;
                      }
                      return (
                        <>
                          {formatValue(item, col.key) !== "Brak" &&
                          col.computePrefix
                            ? col.computePrefix(item)
                            : ""}
                          {formatValue(item, col.key)}
                          {(() => {
                            if (formatValue(item, col.key) === "Brak")
                              return "";
                            if (col.computeSuffix) {
                              const s = col.computeSuffix(item);
                              return s ? s : "";
                            }
                            return col.suffix ?? "";
                          })()}
                        </>
                      );
                    };

                    const isInvestmentPolicy =
                      investmentType.slug === "investment_policy";
                    const isTreasuryBonds =
                      investmentType.slug === "treasury_bonds";
                    const isArtwork = investmentType.slug === "artwork";
                    const isPreciousStones =
                      investmentType.slug === "precious_stones";
                    const isRealEstate = investmentType.slug === "real_estate";
                    const isLoan = investmentType.slug === "loans";
                    const isOther =
                      !isStocksOrShares &&
                      !isInvestmentPolicy &&
                      !isTreasuryBonds &&
                      !isArtwork &&
                      !isPreciousStones &&
                      !isRealEstate &&
                      !isLoan &&
                      !hasTransactions;

                    return (
                      <div
                        key={item.uuid}
                        className={`asset-tile__table-row${
                          hasTransactions ||
                          isStocksOrShares ||
                          isInvestmentPolicy ||
                          isTreasuryBonds ||
                          isArtwork ||
                          isPreciousStones ||
                          isRealEstate ||
                          isLoan ||
                          isOther
                            ? " asset-tile__table-row--clickable"
                            : ""
                        }`}
                        onClick={() => {
                          if (isStocksOrShares) setSelectedShareItem(item);
                          else if (isInvestmentPolicy)
                            setSelectedPolicyItem(item);
                          else if (isTreasuryBonds)
                            setSelectedTreasuryBondsItem(item);
                          else if (isArtwork) setSelectedArtworkItem(item);
                          else if (isPreciousStones)
                            setSelectedPreciousStonesItem(item);
                          else if (isRealEstate)
                            setSelectedRealEstateItem(item);
                          else if (isLoan) setSelectedLoanItem(item);
                          else if (hasTransactions)
                            setSelectedTransactionItem(item);
                          else if (isOther) setSelectedOtherItem(item);
                        }}
                      >
                        {/* Desktop: pozioma tabela */}
                        {columns.map((col) => (
                          <div
                            key={col.key}
                            className="asset-tile__table-cell asset-tile__table-cell--desktop"
                            style={
                              col.width
                                ? {
                                    flex: `0 0 ${col.width}px`,
                                    minWidth: col.width,
                                  }
                                : undefined
                            }
                          >
                            {col.key === columns[0].key &&
                              item.additionalInfo && (
                                <span
                                  className="asset-tile__info-icon"
                                  data-tooltip-id="asset-info-tooltip"
                                  data-tooltip-content={item.additionalInfo}
                                >
                                  <SVG type={SVG_ENUM.INFO} />
                                </span>
                              )}
                            {renderCellContent(col)}
                          </div>
                        ))}
                        {hasFixedWidths && (
                          <div
                            className="asset-tile__table-cell--desktop"
                            style={{ flex: 1 }}
                          />
                        )}
                        <div className="asset-tile__table-cell asset-tile__table-cell--actions asset-tile__table-cell--actions-desktop">
                          <button className="asset-tile__action-btn">
                            <SVG type={SVG_ENUM.ANNOUCEMENT} />
                            <span className="asset-tile__action-badge">0</span>
                          </button>
                          <ContextMenu
                            data={item}
                            position="left"
                            items={[
                              ...(isOwn && !isStocksOrShares
                                ? [
                                    {
                                      name: "Edytuj",
                                      svg: SVG_ENUM.EDIT,
                                      action: (d: any) => setEditItem(d),
                                    },
                                  ]
                                : []),
                              ...(investmentType.slug === "investment_policy"
                                ? [
                                    {
                                      name: "Zamknij aktywo",
                                      svg: SVG_ENUM.CLOSE,
                                      action: (d: any) =>
                                        setCloseInvestmentPolicyItem(d),
                                    },
                                  ]
                                : []),
                              ...(investmentType.slug === "treasury_bonds"
                                ? [
                                    {
                                      name: "Zamknij aktywo",
                                      svg: SVG_ENUM.CLOSE,
                                      action: (d: any) =>
                                        setCloseTreasuryBondsItem(d),
                                    },
                                  ]
                                : []),
                              ...(investmentType.slug === "artwork" ||
                              investmentType.slug === "precious_stones"
                                ? [
                                    {
                                      name: "Zamknij aktywo",
                                      svg: SVG_ENUM.CLOSE,
                                      action: (d: any) => setCloseAssetItem(d),
                                    },
                                  ]
                                : []),
                              ...(investmentType.slug === "real_estate"
                                ? [
                                    {
                                      name: "Zamknij aktywo",
                                      svg: SVG_ENUM.CLOSE,
                                      action: (d: any) =>
                                        setCloseRealEstateItem(d),
                                    },
                                  ]
                                : []),
                              ...(investmentType.slug === "loans"
                                ? [
                                    {
                                      name: "Zamknij aktywo",
                                      svg: SVG_ENUM.CLOSE,
                                      action: (d: any) => setCloseLoanItem(d),
                                    },
                                  ]
                                : []),
                              ...(isOther
                                ? [
                                    {
                                      name: "Zamknij aktywo",
                                      svg: SVG_ENUM.CLOSE,
                                      action: (d: any) => setCloseOtherItem(d),
                                    },
                                  ]
                                : []),
                              ...(isStocksOrShares && item.origin === "other"
                                ? [
                                    {
                                      name: "Edytuj",
                                      svg: SVG_ENUM.EDIT,
                                      action: (d: any) => setEditCompanyItem(d),
                                    },
                                  ]
                                : []),
                              ...(hasTransactions
                                ? [
                                    {
                                      name: "Dodaj  transakcję",
                                      svg: SVG_ENUM.COINS_HAND,
                                      action: (d: any) =>
                                        setAddTransactionItem({
                                          item: d,
                                          defaultType: isOwn ? "sell" : "buy",
                                        }),
                                    },
                                  ]
                                : []),
                              ...(isStocksOrShares
                                ? [
                                    {
                                      name: "Dodaj transakcję",
                                      svg: SVG_ENUM.COINS_HAND,
                                      action: (d: any) => openSellModal(d),
                                    },
                                  ]
                                : []),
                              ...(isOwn && !isStocksOrShares
                                ? [
                                    {
                                      name: "Usuń",
                                      svg: SVG_ENUM.TRASH,
                                      action: (d: any) => setDeleteItem(d),
                                    },
                                  ]
                                : []),
                              ...(isOwn && isStocksOrShares
                                ? [
                                    {
                                      name: "Usuń",
                                      svg: SVG_ENUM.TRASH,
                                      action: (d: any) => setDeleteShareItem(d),
                                    },
                                  ]
                                : []),
                            ]}
                          >
                            <button className="asset-tile__action-btn">
                              <SVG type={SVG_ENUM.DOTS} />
                            </button>
                          </ContextMenu>
                        </div>

                        {/* Mobile: lista par etykieta–wartość */}
                        <div
                          className={`asset-tile__mobile-list${
                            hasTransactions ||
                            isStocksOrShares ||
                            isTreasuryBonds ||
                            isPreciousStones
                              ? " asset-tile__mobile-list--clickable"
                              : ""
                          }`}
                          onClick={() => {
                            if (isStocksOrShares) setSelectedShareItem(item);
                            else if (isTreasuryBonds)
                              setSelectedTreasuryBondsItem(item);
                            else if (isPreciousStones)
                              setSelectedPreciousStonesItem(item);
                            else if (hasTransactions)
                              setSelectedTransactionItem(item);
                          }}
                        >
                          {columns.map((col) => (
                            <div
                              key={col.key}
                              className="asset-tile__mobile-row"
                            >
                              <span className="asset-tile__mobile-label">
                                {col.label ?? columnLabels[col.key] ?? col.key}
                              </span>
                              <span className="asset-tile__mobile-value">
                                {renderCellContent(col)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        <TransactionsSideModal
          isOpen={!!selectedShareItem}
          onClose={() => setSelectedShareItem(null)}
          company={selectedShareItem}
          setEditTransaction={(company) => openEditSharesModal(company)}
          onAddTransactionClick={() => openSellModal(selectedShareItem)}
        />

        <SellSharesModal
          isOpen={!!sellSharesItem}
          onSuccess={returnToSidebar}
          onClose={returnToSidebar}
          hardClose={() => setSellSharesItem(null)}
          company={sellSharesItem}
        />

        <AddSharesModal
          isOpen={!!editSharesItem}
          onSuccess={returnToSidebar}
          onClose={returnToSidebar}
          hardClose={() => setEditSharesItem(null)}
          company={editSharesItem}
        />

        <InvestmentPhotoLightbox
          isOpen={!!lightboxItem}
          onClose={() => setLightboxItem(null)}
          files={(lightboxItem?.files ?? []).filter(
            (f: any) => f.role === "photo",
          )}
          title={lightboxItem?.name}
        />

        <InvestmentTransactionsSideModal
          isOpen={!!selectedTransactionItem}
          onClose={() => setSelectedTransactionItem(null)}
          item={selectedTransactionItem}
          investmentType={investmentType}
          isOwn={isOwn}
          onAddTransaction={(type, currentQuantity) =>
            setAddTransactionItem({
              item: selectedTransactionItem,
              defaultType: type,
              currentQuantity,
            })
          }
          onEditTransaction={(tx, currentQuantity) =>
            setEditTransactionItem({
              item: selectedTransactionItem,
              tx,
              currentQuantity,
            })
          }
        />

        <InvestmentPolicyDetailsSideModal
          isOpen={!!selectedPolicyItem}
          onClose={() => setSelectedPolicyItem(null)}
          item={selectedPolicyItem}
        />

        <TreasuryBondsDetailsSideModal
          isOpen={!!selectedTreasuryBondsItem}
          onClose={() => setSelectedTreasuryBondsItem(null)}
          item={selectedTreasuryBondsItem}
        />

        <ArtworkDetailsSideModal
          isOpen={!!selectedArtworkItem}
          onClose={() => setSelectedArtworkItem(null)}
          item={selectedArtworkItem}
        />

        <PreciousStonesDetailsSideModal
          isOpen={!!selectedPreciousStonesItem}
          onClose={() => setSelectedPreciousStonesItem(null)}
          item={selectedPreciousStonesItem}
        />

        <RealEstateDetailsSideModal
          isOpen={!!selectedRealEstateItem}
          onClose={() => setSelectedRealEstateItem(null)}
          item={selectedRealEstateItem}
        />

        <OtherAssetDetailsSideModal
          isOpen={!!selectedOtherItem}
          onClose={() => setSelectedOtherItem(null)}
          item={selectedOtherItem}
          investmentTypeName={investmentType.name}
        />

        <AddInvestmentTransactionModal
          isOpen={!!addTransactionItem}
          onClose={() => setAddTransactionItem(null)}
          onSuccess={() =>
            setSelectedTransactionItem(addTransactionItem?.item ?? null)
          }
          item={addTransactionItem?.item ?? null}
          investmentType={investmentType}
          defaultType={addTransactionItem?.defaultType ?? "sell"}
          isOwn={isOwn}
          currentQuantity={addTransactionItem?.currentQuantity}
        />

        <AddInvestmentTransactionModal
          isOpen={!!editTransactionItem}
          onClose={() => setEditTransactionItem(null)}
          item={editTransactionItem?.item ?? null}
          investmentType={investmentType}
          defaultType={editTransactionItem?.tx?.type ?? "sell"}
          isOwn={false}
          currentQuantity={editTransactionItem?.currentQuantity}
          editTransaction={editTransactionItem?.tx ?? null}
        />

        <CloseInvestmentPolicyModal
          isOpen={!!closeInvestmentPolicyItem}
          onClose={() => setCloseInvestmentPolicyItem(null)}
          item={closeInvestmentPolicyItem}
        />

        <CloseTreasuryBondsModal
          isOpen={!!closeTreasuryBondsItem}
          onClose={() => setCloseTreasuryBondsItem(null)}
          item={closeTreasuryBondsItem}
        />

        <CloseAssetModal
          isOpen={!!closeAssetItem}
          onClose={() => setCloseAssetItem(null)}
          item={closeAssetItem}
          slug={investmentType.slug}
        />

        <CloseRealEstateModal
          isOpen={!!closeRealEstateItem}
          onClose={() => setCloseRealEstateItem(null)}
          item={closeRealEstateItem}
        />

        <CloseOtherAssetModal
          isOpen={!!closeOtherItem}
          onClose={() => setCloseOtherItem(null)}
          item={closeOtherItem}
        />

        <CloseLoanModal
          isOpen={!!closeLoanItem}
          onClose={() => setCloseLoanItem(null)}
          item={closeLoanItem}
        />

        <LoanDetailsSideModal
          isOpen={!!selectedLoanItem}
          onClose={() => setSelectedLoanItem(null)}
          item={selectedLoanItem}
        />

        <DeleteInvestmentModal
          isOpen={!!deleteItem}
          onClose={() => setDeleteItem(null)}
          item={deleteItem}
        />

        <DeleteCompanyModal
          isOpen={!!deleteShareItem}
          onClose={() => setDeleteShareItem(null)}
          company={deleteShareItem}
        />

        <EditInvestmentModal
          isOpen={!!editItem}
          onClose={() => setEditItem(null)}
          item={editItem}
          slug={investmentType.slug}
        />

        <AddCompanyModal
          isOpen={!!editCompanyItem && isAdmin}
          onClose={() => setEditCompanyItem(null)}
          company={editCompanyItem}
          onSuccess={() => setEditCompanyItem(null)}
        />

        <AddCompanyClientModal
          isOpen={!!editCompanyItem && !isAdmin}
          onClose={() => setEditCompanyItem(null)}
          company={editCompanyItem}
          onSuccess={() => setEditCompanyItem(null)}
        />
      </div>
    </>
  );
};

export default AssetTile;
